<template>
  <div class="w-full">
    <div
      v-if="!settingsOpened"
      class="flex flex-col w-full"
      :class="{ 'cursor-wait': fetchLoading }"
      :style="{ minHeight: 'calc(100vh - 3.75rem - 3rem - 4px)' }"
    >
      <div
        class="sticky top-0 z-10 h-40 px-6 pt-3 border-b bg-pure-white border-slate-400"
      >
        <div class="flex items-center justify-between">
          <BlTextSubtitle2
            text="View target comments and results"
            class="text-slate-700"
          />
          <div class="flex items-center gap-2">
            <BlInputButton
              label="Settings"
              type="outlined"
              dense
              @clicked="settingsOpened = true"
            />
            <BlIcon
              icon="close"
              class="w-6 h-6"
              clickable
              @clicked="$emit('close')"
            />
          </div>
        </div>
        <div class="mt-1">
          <BlTextBody2
            text="View all comments and results recorded for targets during sessions."
          />
        </div>
        <div class="w-full mt-4" :style="{ maxWidth: '100%' }">
          <BlSelectBox
            placeholder="Select a target"
            v-model="targetId"
            :options="targets.map(i => ({ value: i.id, label: i.name }))"
            @change="targetId = $event"
          />
        </div>
      </div>
      <div
        class="flex flex-col gap-4 px-6 overflow-x-auto max-w-132 scrollbar-lg"
      >
        <div v-if="fetchLoading" class="py-4">
          <div class="grid py-32 place-items-center">
            <div class="blum-loader"></div>
          </div>
        </div>
        <div v-else-if="!targetId" class="py-4">
          <div class="flex flex-col py-32 text-center">
            <BlTextBody2 text="No targets selected yet." />
            <BlTextBody2
              text="Please choose a target to view its comments and results."
            />
          </div>
        </div>
        <div v-else-if="!entries.length" class="py-4">
          <div class="py-32 text-center">
            <BlTextBody2 text="Comments(s) not found." />
          </div>
        </div>
        <div v-else class="flex flex-col gap-2">
          <div class="py-2 bg-pure-white" :style="{ zIndex: 1 }">
            <div
              v-if="target.type === 'Target::Prompting' && useResult"
              class="flex mb-4"
            >
              <div
                class="flex px-1 py-1 bg-slate-400"
                :style="{ borderRadius: '8px', height: '2.375rem' }"
              >
                <div
                  v-for="opt in progressByOptions"
                  :key="opt.value"
                  class="flex items-center justify-center flex-shrink-0 h-full transition-all duration-300 rounded cursor-pointer w-30"
                  :class="[
                    progressBy === opt.value ? 'bg-pure-white' : 'bg-slate-400'
                  ]"
                  @click="progressBy = opt.value"
                >
                  <BlTextSubtitle3 :text="opt.label" />
                </div>
              </div>
            </div>
            <div class="flex items-center">
              <div class="flex-shrink-0 w-25">
                <BlTextBody2 text="Date" class="text-slate-700" />
              </div>
              <div v-if="useResult" class="flex items-center">
                <div v-if="target.type === 'Target::Duration'">
                  <BlTextBody2 text="Recorded time" class="text-slate-700" />
                </div>
                <div
                  v-else-if="target.type === 'Target::Prompting'"
                  class="flex items-center gap-2"
                >
                  <div
                    v-if="progressBy === 'final_score'"
                    class="flex-shrink-0 w-20"
                  >
                    <BlTextBody2 text="Final score" class="text-slate-700" />
                  </div>
                  <div v-else class="flex items-center gap-2">
                    <div
                      v-for="prompt in target.prompts"
                      :key="prompt.id"
                      class="flex justify-center flex-shrink-0"
                      :class="{
                        'w-7.5': currentProgressBy === 'score',
                        'w-10': currentProgressBy === 'percentage'
                      }"
                    >
                      <BlTextBody2
                        :text="prompt.abbreviation"
                        class="text-slate-700"
                      />
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="target.type === 'Target::Sbt'"
                  class="flex items-center gap-2"
                >
                  <div
                    v-for="task in target.target_tasks"
                    :key="`task-${task.id}` + task.id"
                    class="flex justify-center flex-shrink-0 w-14"
                  >
                    <BlTextBody2
                      :text="task.code"
                      class="h-4.5 text-slate-700"
                    />
                  </div>
                  <div
                    v-for="pb in target.target_problem_behaviors"
                    :key="`pb-${pb.id}` + pb.id"
                    class="flex justify-center flex-shrink-0 w-14"
                  >
                    <BlTextBody2 :text="pb.code" class="text-slate-700 h-4.5" />
                  </div>
                </div>
                <div v-else class="w-20">
                  <BlTextBody2
                    v-if="target.type === 'Target::Frequency'"
                    text="Frequency"
                    class="text-slate-700"
                  />
                  <BlTextBody2
                    v-else-if="target.type !== 'Target::Sbt'"
                    text="Score"
                    class="text-slate-700"
                  />
                </div>
                <div v-if="target.type === 'Target::Pir'">
                  <BlTextBody2 text="Percentage" class="text-slate-700" />
                </div>
              </div>
              <div v-else>
                <BlTextBody2 text="Comment" class="text-slate-700" />
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div v-for="entry in filteredEntries" :key="entry.id">
              <div
                v-if="entry.type === 'Entry::PhaseLine'"
                class="flex items-center gap-2 py-2"
              >
                <div class="w-full bg-slate-300 h-0.5 rounded-full"></div>
                <div class="flex items-center flex-shrink-0 gap-2">
                  <div
                    v-if="entry.target_type !== 'Target::Prompting'"
                    class="flex-shrink-0 w-5 h-5 rounded-full"
                    :style="{
                      backgroundColor: phaseLineColors[entry.line_color]
                    }"
                  ></div>
                  <BlTextSubtitle3 :text="entry.line_label" />
                </div>
                <div class="w-full bg-slate-300 h-0.5 rounded-full"></div>
              </div>
              <div v-else class="flex py-2 border-b border-slate-400">
                <div class="flex-shrink-0 w-25">
                  <BlTextBody2
                    :text="displayDate({ date: entry.recorded_at })"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <div v-if="useResult" class="flex">
                    <div v-if="entry.type === 'Entry::Duration'">
                      <BlTextBody2 :text="entry.recorded_time" />
                    </div>
                    <div
                      v-else-if="entry.type === 'Entry::Prompting'"
                      class="flex items-center gap-2"
                    >
                      <div
                        v-if="progressBy === 'final_score'"
                        class="flex justify-center flex-shrink-0 w-20"
                      >
                        <BlTextBody2 :text="`${entry.score}%`" />
                      </div>
                      <div v-else class="flex items-center gap-2">
                        <div
                          v-for="prompt in target.prompts"
                          :key="prompt.id"
                          class="h-7.5 flex-shrink-0 flex items-center justify-center"
                          :class="{
                            'w-7.5': currentProgressBy === 'score',
                            'w-10': currentProgressBy === 'percentage'
                          }"
                          :style="{
                            backgroundColor: entry.scores[prompt.id][
                              currentProgressBy
                            ]
                              ? promptColors[prompt.color].primaryColor
                              : 'transparent'
                          }"
                        >
                          <BlTextBody2
                            :text="
                              `${entry.scores[prompt.id][currentProgressBy]}${
                                currentProgressBy === 'percentage' ? '%' : ''
                              }`
                            "
                            :style="{
                              color: entry.scores[prompt.id][currentProgressBy]
                                ? promptColors[prompt.color].textColor
                                : ''
                            }"
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      v-else-if="entry.type === 'Entry::Sbt'"
                      class="flex items-center gap-2"
                    >
                      <div
                        v-for="task in target.target_tasks"
                        :key="`task-${task.id}`"
                        :id="task.id"
                        class="w-14 h-7.5 flex-shrink-0 flex items-center justify-center"
                        :style="{
                          backgroundColor: getAverageScore(entry, task.id)
                            ? task.color
                            : 'transparent'
                        }"
                      >
                        <BlTextBody2
                          :text="getAverageScore(entry, task.id)"
                          :style="{
                            color: getAverageScore(entry, task.id)
                              ? '#FFFFFF'
                              : '#A0A0A0'
                          }"
                        />
                      </div>
                      <div
                        v-for="pb in target.target_problem_behaviors"
                        :key="`pb-${pb.id}`"
                        :id="pb.id"
                        class="flex justify-center flex-shrink-0 w-14"
                      >
                        <div
                          v-if="getRecordProblemBehaviors(entry, pb.id) !== 0"
                          class="flex items-center justify-center flex-shrink-0 w-12 h-8 cursor-pointer"
                          :style="{
                            backgroundColor: pb.color
                          }"
                        >
                          <BlTextBody2
                            :text="getRecordProblemBehaviors(entry, pb.id)"
                            :style="{
                              color: '#FFFFFF'
                            }"
                          />
                        </div>
                      </div>
                    </div>
                    <div v-else class="w-20">
                      <div
                        v-if="target.type === 'Target::Frequency'"
                        class="w-7.5 h-7.5 flex-shrink-0 flex items-center justify-center"
                        :class="{ 'bg-light-purple-300': entry.score }"
                      >
                        <BlTextBody2
                          :text="entry.score"
                          :class="{ 'text-pure-white': entry.score }"
                        />
                      </div>
                      <BlTextBody2 v-else :text="entry.score" />
                    </div>
                    <div v-if="entry.type === 'Entry::Pir'">
                      <BlTextBody2 :text="`${entry.percentage}%`" />
                    </div>
                  </div>
                  <div v-if="useComment" class="w-full">
                    <BlTextBody2 :text="entry.comment || '-'" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="settingsOpened" class="flex flex-col">
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-slate-400"
      >
        <BlTextSubtitle2 text="Settings" class="text-slate-700" />
      </div>
      <div class="flex flex-col gap-3 px-6 py-4">
        <div class="flex flex-col gap-1">
          <BlTextSubtitle3 text="Sort by" />
          <div
            v-for="opt in sortByOptions"
            :key="opt.value"
            class="flex gap-2 pl-6 cursor-pointer"
            @click="sortByInput = opt.value"
          >
            <BlInputRadio
              :checked="sortByInput === opt.value"
              name="select_sort_by"
            />
            <div class="flex flex-col gap-1">
              <BlTextBody2 :text="opt.label" />
              <BlTextBody2
                v-if="opt.description"
                :text="opt.description"
                class="whitespace-pre-line text-slate-700"
              />
            </div>
          </div>
        </div>
        <div class="h-0.5 w-full flex-shrink-0 bg-slate-200 rounded"></div>
        <div class="flex flex-col gap-1">
          <BlTextSubtitle3 text="Session display" />
          <div
            v-for="opt in sessionDisplayOptions"
            :key="opt.value"
            class="flex gap-2 pl-6 cursor-pointer"
            @click="sessionDisplayInput = opt.value"
          >
            <BlInputRadio
              :checked="sessionDisplayInput === opt.value"
              name="select_session_display"
            />
            <div class="flex flex-col gap-1">
              <BlTextBody2 :text="opt.label" />
              <BlTextBody2
                v-if="opt.description"
                :text="opt.description"
                class="whitespace-pre-line text-slate-700"
              />
            </div>
          </div>
        </div>
        <div class="h-0.5 w-full flex-shrink-0 bg-slate-200 rounded"></div>
        <div class="flex flex-col gap-1">
          <BlTextSubtitle3 text="View options" />
          <div
            v-for="opt in viewOptionOptions"
            :key="opt.value"
            class="flex gap-2 pl-6 cursor-pointer"
            @click="viewOptionInput = opt.value"
          >
            <BlInputRadio
              :checked="viewOptionInput === opt.value"
              name="select_view_option"
            />
            <div class="flex flex-col gap-1">
              <BlTextBody2 :text="opt.label" />
              <BlTextBody2
                v-if="opt.description"
                :text="opt.description"
                class="whitespace-pre-line text-slate-700"
              />
            </div>
          </div>
        </div>
        <div class="h-0.5 w-full flex-shrink-0 bg-slate-200 rounded"></div>
      </div>
      <div class="grid items-center grid-cols-2 gap-4 px-6 py-4">
        <BlInputButton
          label="Cancel"
          type="plain"
          @clicked="settingsOpened = false"
        />
        <BlInputButton label="Update" @clicked="onSaveSettings" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import { PROMPT_COLORS, PHASE_LINE_COLORS } from "@/plugins/assets.js";

import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "ReportViewComments",
  components: {
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputButton,
    BlInputRadio,
    BlSelectBox,
    BlIcon
  },
  props: {
    settings: { type: Object },
    selectedTargetId: { type: Number, default: 0 }
    // @change-settings
    // @set-target-id
    // @close
  },
  data() {
    return {
      promptColors: PROMPT_COLORS,
      phaseLineColors: PHASE_LINE_COLORS,
      fetchLoading: false,
      targetId: 0,
      target: {
        id: 0,
        name: "",
        type: "",
        goal: "",
        date_mastered: "",
        prompting_format: "classic"
      },
      entries: [],
      //
      settingsOpened: false,
      sortByInput: "oldest",
      sortByOptions: [
        { value: "oldest", label: "Oldest session", description: "" },
        { value: "newest", label: "Newest session", description: "" }
      ],
      sessionDisplayInput: "all",
      sessionDisplayOptions: [
        {
          value: "all",
          label: "Show all sessions",
          description:
            "Displays all sessions, including those without comments."
        },
        {
          value: "with-comment",
          label: "Show sessions with comments",
          description: "Only shows sessions that include comments."
        }
      ],
      viewOptionInput: "all",
      viewOptionOptions: [
        {
          value: "all",
          label: "Show all data",
          description: "Displays both session results and comments."
        },
        {
          value: "result",
          label: "Show results only",
          description: "Displays only the session results."
        },
        {
          value: "comment",
          label: "Show comments only",
          description: "Displays only the comments, without session results."
        }
      ],
      //
      progressBy: "score", // frequency | percentage | final_score
      problemBehaviorRecords: []
    };
  },
  computed: {
    ...mapGetters(["report", "targets"]),
    currentProgressBy() {
      return this.progressBy === "final_score" ? "score" : this.progressBy;
    },
    progressByOptions() {
      const arr = [
        { value: "score", label: "By frequency" },
        { value: "percentage", label: "By percentage" }
      ];
      if (this.target.prompting_format === "custom") {
        arr.unshift({
          value: "final_score",
          label: "By final score"
        });
      }
      return arr;
    },
    filteredEntries() {
      let arr = [...this.entries];
      if (this.settings.session_display === "with-comment") {
        arr = arr.filter(i => i.comment && i.comment !== "\n");
      }
      if (this.settings.sort_by === "newest") {
        arr = arr.reverse();
      }
      return arr;
    },
    useComment() {
      if (this.settings.view_option === "all") return true;
      if (this.settings.view_option === "comment") return true;
      return false;
    },
    useResult() {
      if (this.settings.view_option === "all") return true;
      if (this.settings.view_option === "result") return true;
      return false;
    }
  },
  watch: {
    selectedTargetId(val) {
      if (val && val !== this.targetId) {
        this.targetId = val;
      }
    },
    targetId(val) {
      if (val && val !== this.target.id) {
        this.progressBy = "score";
        this.fetchTarget();
        this.$emit("set-target-id", val);
      }
    },
    settingsOpened(val) {
      if (val) {
        this.sortByInput = this.settings.sort_by;
        this.sessionDisplayInput = this.settings.session_display;
        this.viewOptionInput = this.settings.view_option;
      }
    }
  },
  mounted() {
    if (this.selectedTargetId && this.selectedTargetId !== this.targetId) {
      this.targetId = this.selectedTargetId;
    }
  },
  methods: {
    ...mapActions(["getTarget"]),
    displayDate,
    async fetchTarget() {
      this.fetchLoading = true;
      const { success, data } = await this.getTarget({ id: this.targetId });
      this.fetchLoading = false;
      if (!success) return;
      this.target = data;
      this.entries = data.entries;
    },
    onSaveSettings() {
      const data = {
        sort_by: this.sortByInput,
        session_display: this.sessionDisplayInput,
        view_option: this.viewOptionInput
      };
      this.$emit("change-settings", data);
      this.settingsOpened = false;
    },
    getAverageScore(entry, taskId) {
      if (!entry.scores || typeof entry.scores !== "object") {
        return taskId ? "0" : {};
      }

      const scoreGroups = {};

      Object.values(entry.scores).forEach(score => {
        if (!score || typeof score !== "object") return;

        const id = score.target_task_id;
        if (!id) return;

        if (!scoreGroups[id]) {
          scoreGroups[id] = [];
        }
        scoreGroups[id].push(Number(score.prompt_score || 0));
      });

      const averages = {};
      Object.keys(scoreGroups).forEach(id => {
        if (scoreGroups[id].length === 0) {
          averages[id] = 0;
          return;
        }

        const totalScore = scoreGroups[id].reduce((acc, curr) => acc + curr, 0);
        averages[id] = Math.round(totalScore / scoreGroups[id].length);
      });

      if (taskId) {
        const average = averages[taskId] || 0;
        return average > 0 ? `${average}%` : "0%";
      }

      return averages;
    },
    getRecordProblemBehaviors(entry, pbId) {
      if (typeof entry.scores === "object" && entry.scores !== null) {
        let total = 0;

        const pbCode = this.target.target_problem_behaviors.find(
          pb => pb.id === pbId
        );

        if (!pbCode.code) return 0;

        Object.values(entry.scores).forEach(score => {
          if (
            score.problem_behavior_code === pbCode.code &&
            score.target_problem_behavior_id === pbId
          ) {
            total++;
          }
        });

        if (!this.problemBehaviorRecords) {
          this.problemBehaviorRecords = [];
        }

        const existingIndex = this.problemBehaviorRecords.findIndex(
          r => r.code === pbCode.code
        );
        if (existingIndex >= 0) {
          this.problemBehaviorRecords[existingIndex].total = total;
        } else {
          this.problemBehaviorRecords.push({ code: pbCode.code, total });
        }

        return total;
      }

      return 0;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/reports/helper/ViewComments.vue